import { useCallback, useEffect, useRef, useState } from 'react';

const API_SRC = 'https://www.youtube.com/iframe_api';

// Resolve once the IFrame API is ready, chaining onto any existing callback so
// two mounts cannot clobber each other.
const ytReady = (fn) => {
  if (window.YT && window.YT.Player) {
    fn();
    return;
  }
  if (!document.querySelector(`script[src="${API_SRC}"]`)) {
    const tag = document.createElement('script');
    tag.src = API_SRC;
    document.head.appendChild(tag);
  }
  const prev = window.onYouTubeIframeAPIReady;
  window.onYouTubeIframeAPIReady = () => {
    if (prev) prev();
    fn();
  };
};

/**
 * Mounts a real YouTube playlist player and reads its queue back out, so the
 * rail beside it lists the playlist's actual videos and follows playback.
 *
 * Returns `failed` when the API never arrives (blocked script, offline), so the
 * caller can fall back to a plain nocookie iframe.
 */
export const useYouTubePlaylist = (listId) => {
  const mountRef = useRef(null);
  const playerRef = useRef(null);
  const listRef = useRef(null);
  const pollRef = useRef(null);

  const [ids, setIds] = useState([]);
  const [titles, setTitles] = useState({});
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState(false);

  const fetchTitles = useCallback((videoIds) => {
    videoIds.forEach((id) => {
      setTitles((prev) => {
        if (prev[id]) return prev;
        fetch(`https://www.youtube.com/oembed?format=json&url=https://www.youtube.com/watch?v=${id}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => d && d.title && setTitles((t) => ({ ...t, [id]: d.title })))
          .catch(() => {});
        return prev;
      });
    });
  }, []);

  const readPlaylist = useCallback(() => {
    const player = playerRef.current;
    if (!player || !player.getPlaylist) return;
    const next = player.getPlaylist() || [];
    if (!next.length) return;
    setIds((prev) => {
      if (prev.join(',') === next.join(',')) return prev;
      fetchTitles(next);
      return next;
    });
    const at = player.getPlaylistIndex ? player.getPlaylistIndex() : 0;
    if (at >= 0) setIndex(at);
  }, [fetchTitles]);

  useEffect(() => {
    if (!listId) return undefined;

    let alive = true;
    // Captured for the cleanup: the ref may point elsewhere by then.
    const mount = mountRef.current;
    listRef.current = listId;
    setIds([]);
    setIndex(0);
    setFailed(false);

    // If the API never arrives, fall back to a plain iframe.
    const giveUp = setTimeout(() => alive && !playerRef.current && setFailed(true), 6000);

    ytReady(() => {
      if (!alive || !mount || listRef.current !== listId) return;
      clearTimeout(giveUp);
      mount.innerHTML = '';
      const host = document.createElement('div');
      host.style.width = '100%';
      host.style.height = '100%';
      mount.appendChild(host);

      playerRef.current = new window.YT.Player(host, {
        width: '100%',
        height: '100%',
        host: 'https://www.youtube-nocookie.com',
        playerVars: { list: listId, listType: 'playlist', rel: 0, modestbranding: 1, playsinline: 1 },
        events: {
          onReady: () => {
            // The queue is not populated the instant the player is ready.
            let tries = 0;
            pollRef.current = setInterval(() => {
              tries += 1;
              const queue = playerRef.current?.getPlaylist?.() || [];
              if (queue.length || tries > 20) {
                clearInterval(pollRef.current);
                pollRef.current = null;
                readPlaylist();
              }
            }, 250);
          },
          onStateChange: readPlaylist,
        },
      });
    });

    return () => {
      alive = false;
      clearTimeout(giveUp);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      // cuePlaylist does not reliably replace a queue that came from
      // playerVars.list, so the player is rebuilt for every playlist.
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          /* already gone */
        }
        playerRef.current = null;
      }
      if (mount) mount.innerHTML = '';
      listRef.current = null;
    };
  }, [listId, readPlaylist]);

  const playAt = useCallback((i) => {
    if (playerRef.current?.playVideoAt) {
      playerRef.current.playVideoAt(i);
      setIndex(i);
    }
  }, []);

  return { mountRef, ids, titles, index, failed, playAt };
};
